import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { BadgeCheck, ChevronRight } from 'lucide-react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { AsyncContent } from '@/components/shared/AsyncContent';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { RequireActiveOrganization } from '@/components/shared/RequireActiveOrganization';
import { HubReadGuard } from '@reflex/ui/components/layout/NoReadAccessState';
import { api } from '@/lib/api';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { OrganizationRoleDetailPage } from './OrganizationRoleDetailPage';

/**
 * Roles list, with role detail rendered in place when the URL carries
 * `/roles/:roleId` (Back chip returns to the list without leaving Roles).
 */
export function OrganizationRolesPage() {
  const { roleId } = useParams<{ roleId?: string }>();
  if (roleId) {
    return (
      <HubReadGuard permission="org:read">
        <OrganizationRoleDetailPage />
      </HubReadGuard>
    );
  }

  return (
    <HubReadGuard permission="org:read">
      <RequireActiveOrganization title="Roles">
        {(org) => <RolesBody key={org.id} orgId={org.id} />}
      </RequireActiveOrganization>
    </HubReadGuard>
  );
}

function RolesBody({ orgId }: { orgId: string }) {
  const routes = useOrgRoutes();
  const rolesQuery = useQuery({
    queryKey: ['organizations', orgId, 'roles'],
    queryFn: () => api.listOrganizationRoles(orgId),
    enabled: !!orgId,
  });

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        iconVariant="box"
        icon={<BadgeCheck className="h-5 w-5 text-muted-foreground" />}
        title="Roles"
        description="Built-in and custom roles available in this organization."
        docs="administration/organization-teams#roles-permissions"
      />
      <SectionCard
        title="All roles"
        description="Built-in roles ship with sensible defaults; custom roles can be tailored to your workflows."
        data-testid="roles-section"
      >
        <AsyncContent
          isLoading={rolesQuery.isLoading}
          isError={rolesQuery.isError}
          loadingMessage="Loading roles…"
          errorMessage="Failed to load roles."
        >
          {rolesQuery.data && rolesQuery.data.length > 0 ? (
            <ul className="divide-y divide-border rounded-md border">
              {rolesQuery.data.map((role) => (
                <li key={role.id} data-testid={`role-row-${role.id}`}>
                  <Link
                    to={routes.role(role.id)}
                    aria-label={`View role ${role.slug}`}
                    className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{role.name}</span>
                        <span className="text-xs uppercase text-muted-foreground">
                          {role.scope} {role.isBuiltin ? '· built-in' : ''}
                        </span>
                      </div>
                      {role.description && (
                        <p className="text-sm text-muted-foreground">{role.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {role.permissions.length} permission
                        {role.permissions.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <ChevronRight className="icon-sm shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState variant="bordered" title="No roles defined" />
          )}
        </AsyncContent>
      </SectionCard>
    </PageLayout>
  );
}
